import { AlertTriangle, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

import { type ParsedApiError } from "@/lib/error-handler";
import { cn } from "@/lib/utils";

import { Button } from "../ui/button";

interface ErrorRetryComponentProps {
  error?: ParsedApiError | null;
  retry: () => void;
  direction?: "horizontal" | "vertical";
  className?: string;
}

export function ErrorRetryComponent({
  error,
  retry,
  direction = "horizontal",
  className = "",
}: ErrorRetryComponentProps) {
  const { t } = useTranslation();
  const isVertical = direction === "vertical";

  return (
    <div
      role="alert"
      className={cn(
        "flex gap-3 py-1 text-md",
        isVertical ? "flex-col items-start" : "items-start justify-between",
        className
      )}
    >
      <div className="flex items-start gap-2 text-foreground">
        <AlertTriangle className="h-4 w-4 mt-1 shrink-0 text-destructive" />
        {error ? (
          <div className="flex flex-col gap-1">
            <span className="font-medium">{`${error.title} (${error.code})`}</span>
            <span>{error.details}</span>
            {error.traceId && (
              <span className="italic text-gray-500">{error.traceId}</span>
            )}
          </div>
        ) : (
          <span>{t("loading_failed")}</span>
        )}
      </div>
      <Button
        type="button"
        size="default"
        variant="ghost"
        className="font-medium text-foreground underline-offset-2 hover:underline"
        onClick={retry}
        aria-label="retry"
      >
        <RefreshCw className="h-4 w-4 shrink-0" />
        {t("retry")}
      </Button>
    </div>
  );
}
